import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useExchangeRates } from "../context/ExchangeRateContext";
import { formatNumber } from "../utils/formatters";
import { Density, DENSITY_LEVELS, getDensity, setDensity } from "../utils/density";
import NotificationBell from "./NotificationBell";
import "./Header.css";

interface HeaderProps {
  onMenuClick?: () => void;
}

/** Rol kodu → görünen ad */
const roleLabel = (role?: string) => {
  switch (role?.toLowerCase()) {
    case "admin": return "Yönetici";
    case "manager": return "Müdür";
    case "modelist": return "Modelist";
    case "accounting": return "Muhasebe";
    default: return role || "Kullanıcı";
  }
};

/**
 * Üst çubuk — kurumsal çizgi: 4rem yükseklik, beyaz zemin, ince çizgi, lacivert vurgu.
 * Sol: menü + marka · Sağ: kur çipleri, görünüm boyutu, kullanıcı menüsü.
 */
const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { usdRate, eurRate, loading } = useExchangeRates();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [density, setDensityState] = useState<Density>(getDensity);

  const handleDensityChange = (value: Density) => {
    setDensity(value); // <html data-density> + localStorage — rem tabanlı, canlı uygulanır
    setDensityState(value);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Dışarı tıklama / Esc → menüyü kapat
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const initials = (() => {
    if (!user) return "?";
    const f = user.firstName?.charAt(0) || "";
    const l = user.lastName?.charAt(0) || "";
    return (f + l).toUpperCase() || "?";
  })();

  const hasRates = !loading && (usdRate || eurRate);

  return (
    <header className="nh">
      <div className="nh-inner">
        {/* ── Sol: menü + marka ── */}
        <div className="nh-left">
          {onMenuClick && (
            <button className="nh-menu" onClick={onMenuClick} aria-label="Menüyü aç" title="Menü">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </button>
          )}
          <span className="nh-sep" aria-hidden="true" />
          <a className="nh-brand" href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} aria-label="Nedaris — ana sayfa">
            <img src="/brand/nedaris-lockup.svg" alt="Nedaris" className="nh-lockup" />
            <img src="/brand/nedaris-mark.svg" alt="" className="nh-mark" aria-hidden="true" />
          </a>
        </div>

        {/* ── Sağ: kurlar · görünüm · kullanıcı ── */}
        <div className="nh-right">
          {hasRates && (
            <div className="nh-rates" aria-label="Güncel kurlar">
              {usdRate && (
                <span className="nh-rate">
                  <span className="nh-rate-code">USD</span>
                  <span className="nh-rate-val">{formatNumber(usdRate)} ₺</span>
                </span>
              )}
              {eurRate && (
                <span className="nh-rate">
                  <span className="nh-rate-code">EUR</span>
                  <span className="nh-rate-val">{formatNumber(eurRate)} ₺</span>
                </span>
              )}
            </div>
          )}

          {user && <NotificationBell />}

          <div className="nh-density" role="group" aria-label="Görünüm boyutu">
            {DENSITY_LEVELS.map((lvl) => (
              <button
                key={lvl.value}
                className={`nh-density-btn is-${lvl.value} ${density === lvl.value ? "active" : ""}`}
                onClick={() => handleDensityChange(lvl.value)}
                title={`${lvl.label} — ${lvl.hint}`}
                aria-pressed={density === lvl.value}
              >
                A
              </button>
            ))}
          </div>

          {user && (
            <div className="nh-user" ref={menuRef}>
              <button
                className={`nh-user-btn ${menuOpen ? "open" : ""}`}
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <span className="nh-avatar">{initials}</span>
                <span className="nh-user-text">
                  <span className="nh-user-name">{user.firstName} {user.lastName}</span>
                  <span className="nh-user-role">{roleLabel(user.role)}</span>
                </span>
                <svg className="nh-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {menuOpen && (
                <div className="nh-dropdown" role="menu">
                  <div className="nh-dd-head">
                    <span className="nh-avatar lg">{initials}</span>
                    <div className="nh-dd-id">
                      <span className="nh-dd-name">{user.firstName} {user.lastName}</span>
                      <span className="nh-dd-mail">{user.email}</span>
                      <span className="nh-dd-role">{roleLabel(user.role)}</span>
                    </div>
                  </div>

                  {/* Kurlar — üst çubukta yer kalmayan dar ekranlar için */}
                  {hasRates && (
                    <div className="nh-dd-rates">
                      {usdRate && (
                        <div className="nh-dd-rate">
                          <span className="nh-dd-rate-code">USD / TRY</span>
                          <span className="nh-dd-rate-val">{formatNumber(usdRate)} ₺</span>
                        </div>
                      )}
                      {eurRate && (
                        <div className="nh-dd-rate">
                          <span className="nh-dd-rate-code">EUR / TRY</span>
                          <span className="nh-dd-rate-val">{formatNumber(eurRate)} ₺</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="nh-dd-list">
                    <button
                      className="nh-dd-item"
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); navigate("/change-password"); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      <span>Şifre Değiştir</span>
                    </button>
                  </div>

                  <button className="nh-dd-item nh-dd-logout" role="menuitem" onClick={handleLogout}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    <span>Çıkış Yap</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
